import { Link } from "react-router-dom";
import { ShieldCheck, Users, Award, MapPin, Heart, ArrowRight, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { useSite } from "@/context/SiteContext";
import { waLink, generalWaMessage } from "@/lib/whatsapp";

const VALUES = [
  { icon: ShieldCheck, title: "Authentic Authority", text: "Hereditary priests serving the Gokarna Mahabaleshwar Temple for generations." },
  { icon: Users, title: "Expert Team", text: "Experienced pandits and astrologers offering genuine spiritual guidance." },
  { icon: Award, title: "Transparent & Honest", text: "Clear pricing and truthful advice — we never push unnecessary rituals." },
  { icon: MapPin, title: "Local Presence", text: "Rooted in Gokarna, available to devotees across India." },
];

const About = () => {
  const site = useSite();
  const img = "https://images.pexels.com/photos/31969428/pexels-photo-31969428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1100";
  return (
    <Layout>
      <Seo
        title="About Shrigokarnam | Authentic Priest Team in Gokarna"
        description="Shrigokarnam is led by hereditary priests, expert pandits and astrologers at the Gokarna Mahabaleshwar Temple, offering authentic poojas with transparent pricing and honest guidance."
      />
      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gold">Why Choose Us</span>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl text-white tracking-tight">The Authentic Priest-Led Choice in Gokarna</h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <img src={img} alt="Gokarna temple" className="rounded-xl object-cover w-full aspect-[4/3]" />
        <div>
          <h2 className="font-heading text-4xl text-secondary tracking-tight">Devotee-First, Always</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{site.about}</p>
          <p className="mt-4 text-muted-foreground leading-relaxed">We built Shrigokarnam to remove the confusion, fear, and unreliable middlemen that devotees often face. With us, you get direct access to the priest team, honest guidance on which pooja you truly need, transparent pricing, and quick support on WhatsApp.</p>
          <Link to="/poojas" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204]" data-testid="about-view-poojas"><Heart className="h-4 w-4" /> Explore Our Poojas</Link>
        </div>
      </section>

      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <div key={v.title} className="reveal bg-white border border-border rounded-xl p-7" style={{ animationDelay: `${i * 0.07}s` }}>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><v.icon className="h-6 w-6" /></span>
              <h3 className="mt-4 font-heading text-xl text-secondary">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="font-heading text-3xl text-white">Have a question before booking?</h2>
          <div className="flex gap-3">
            <a href={waLink(site.whatsapp_number, generalWaMessage)} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-6 py-3 font-semibold text-white hover:opacity-90" data-testid="about-whatsapp"><Phone className="h-4 w-4" /> WhatsApp Us</a>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204]" data-testid="about-contact">Send Enquiry <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
